import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useUpdatePatient } from '@avanta/shared/hooks/useUpdatePatient';
import { type Patient } from '@avanta/shared/types/patients';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { PatientService } from 'services/patients';
import { useUserStore } from 'stores/user';
import { inputs, type PersonalDataForm } from './model';
import { useFetchPatient } from './useFetchPatient';

const MAX_ATTEMPTS = 6;
const POLLING_INTERVAL = 3000;

export const AppointmentsPersonalData = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    reset,
    register,
    formState: { errors, isValid },
  } = useForm<PersonalDataForm>({
    mode: 'onChange',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { attempts } = useFetchPatient({
    refetchInterval: isSubmitted ? POLLING_INTERVAL : undefined,
    maxAttempts: MAX_ATTEMPTS,
  });
  const patient = useUserStore(state => state.user);
  const { mutate: updatePatient } = useUpdatePatient(false);

  useEffect(() => {
    if (patient && Object.keys(patient).length > 0) {
      reset({
        firstName: patient.firstName,
        surname: patient.surname,
        nif: patient.nif,
        contactPhone: patient.contactPhone,
      });
    }
  }, [patient, reset]);

  useEffect(() => {
    if (
      isSubmitted &&
      (patient?.profileCompleteToBookAppointments || Number(attempts) >= MAX_ATTEMPTS)
    ) {
      navigate(ROUTES.APPOINTMENTS_RESERVE);
    }
  }, [patient?.profileCompleteToBookAppointments, navigate, isSubmitted, attempts]);

  const handleOnSubmit = async (data: PersonalDataForm): Promise<void> => {
    setIsSubmitted(false);
    const patientData = {
      ...patient,
      ...data,
    } as Patient;

    if (patient?.profileCompleteToBookAppointments) {
      updatePatient(patientData);
    } else {
      try {
        await PatientService.updateBookingInfo(patientData);
      } catch {
        toast.error(t('AppointmentsPersonalData.update.error'));
        return;
      }
    }
    setIsSubmitted(true);
  };

  if (isSubmitted && !patient?.profileCompleteToBookAppointments) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-denim-600">
          {t('AppointmentsPersonalData.heading')}
        </h2>
        <p className="text-sm text-gray-600">{t('AppointmentsPersonalData.body')}</p>
      </div>
      <form className="flex flex-col gap-6 items-center" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid grid-cols-2 gap-4 w-full max-w-md max-am-lg:grid-cols-1">
          {Object.entries(inputs).map(([id, input]) => {
            const formId = id as keyof PersonalDataForm;
            const formErrors = errors?.[formId];
            const Component = input.component ?? Input;
            return (
              <div key={formId}>
                <Component
                  type={input?.type}
                  control={control}
                  label={t(input?.label)}
                  placeholder={t(input?.placeholder ?? '')}
                  helperText={formErrors?.message ? t(formErrors.message) : undefined}
                  variant={!!formErrors ? 'invalid' : 'filled'}
                  {...(input?.defaultCountry && { defaultCountry: 'ES' })}
                  rules={{
                    required: input.required,
                    ...(input.pattern && { pattern: input.pattern }),
                    ...(input.validate && { validate: input.validate }),
                    ...(input.minLength && { minLength: input.minLength }),
                  }}
                  {...register(formId, {
                    required: input?.required,
                    pattern: input?.pattern,
                    validate: input?.validate,
                    minLength: input?.minLength,
                  })}
                />
              </div>
            );
          })}
        </div>
        <Button type="submit" className="w-fit" disabled={!isValid}>
          {t('AppointmentsReserve.next')}
        </Button>
      </form>
    </div>
  );
};
